import React, { useState } from 'react';
import { range } from 'lodash-es';
import { Box, Divider, FormField, Input, Label, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';
import CurrencyInput from '../../Widgets/CurrencyInput';

import { Field, FixturesAppliancesPersonalPropertySection } from '../../../models/index';
import { FIXTURES_APPLIANCES_PERSONAL_PROPERTY } from '../../../constants/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY = 20;

type Props = {
  section: FixturesAppliancesPersonalPropertySection;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: FixturesAppliancesPersonalPropertySection) => void;
};

function FixturesAppliancesPersonalProperty({ section, setSectionValues, setSection }: Props): JSX.Element {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: FixturesAppliancesPersonalPropertySection,
  });

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setCost(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.COST, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderAdditionalFixturesAppliancesPersonalProperty(index: number): React.ReactNode {
    const nameField = section.getField(
      Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.NAME, index),
    );
    const costField = section.getField(
      Field.getIndexedFieldName(FIXTURES_APPLIANCES_PERSONAL_PROPERTY.FIELD_NAME.COST, index),
    );

    return (
      <Box>
        {index > 0 && (
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              marginTop: 'layout.tighter',
              marginBottom: 'layout.tighter',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center', height: '1rem' }}>
              <Text textStyle="body-bold">Included item #{index + 1}</Text>
            </Box>
            <Box css={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '1rem' }}>
              <TextButton data-index={index} data-test="delete-fixture" onClick={onDeleteOne}>
                Delete
              </TextButton>
            </Box>
          </Box>
        )}
        <Box css={{ display: 'grid', gridTemplateColumns: { base: '1fr', sm: '7fr 5fr' }, gap: 'layout.tighter' }}>
          <Box>
            <FormField
              required
              css={{ marginBottom: 'layout.tighter' }}
              label={<Label id={`item-description-${index}`}>Item description</Label>}
              control={
                <Input
                  aria-labelledby={`item-description-${index}`}
                  data-index={index}
                  data-test="item-name"
                  value={nameField.enteredValue}
                  onChange={setName}
                />
              }
            />
          </Box>
          <Box>
            <FormField
              required
              label={<Label id={`replacement-cost-${index}`}>Replacement cost</Label>}
              control={
                <CurrencyInput
                  aria-labelledby={`replacement-cost-${index}`}
                  data-test="cost"
                  value={costField.enteredValue || ''}
                  onChange={setCost}
                />
              }
            />
          </Box>
        </Box>
      </Box>
    );
  }

  const totalFixturesAppliancesPersonalProperty = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(
    section,
    'fixturesAppliancesPersonalProperty0_isEnabled',
    hasClickedNo,
  );

  if (totalFixturesAppliancesPersonalProperty == null) {
    return null;
  }

  return (
    <>
      <FormField
        required
        label={
          <>
            <Label>Do you have any items on the property to itemize?</Label>
            <Paragraph css={{ marginBottom: 'layout.tighter' }}>
              You must list all fixtures, appliances, personal property, and equipment, along with their replacement
              costs.
            </Paragraph>
          </>
        }
        control={
          <YesAndNoExpansion yesHandler={onAddOne} noHandler={onRemoveAll} yesActive={isEnabled}>
            <Box css={{ marginTop: 'lg' }} />
            {range(0, totalFixturesAppliancesPersonalProperty).map((index: number) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
                {renderAdditionalFixturesAppliancesPersonalProperty(index)}
              </React.Fragment>
            ))}
            {totalFixturesAppliancesPersonalProperty < MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY ? (
              totalFixturesAppliancesPersonalProperty > 0 && (
                <TextButton data-test="add-another" icon={<IconPlusFilled />} onClick={onAddOne}>
                  Add another item
                </TextButton>
              )
            ) : (
              <Text>
                <span data-test="maximum-reached">
                  Youve added {MAXIMUM_ADDITIONAL_FIXTURES_APPLIANCES_PERSONAL_PROPERTY} items, which is the maximum
                  allowed.
                </span>
              </Text>
            )}
          </YesAndNoExpansion>
        }
      />
    </>
  );
}

export default FixturesAppliancesPersonalProperty;
